/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig, ResponseType } from 'axios';
import { appStore } from '../AppStore';
import { NotificationHandler } from '../Components/ToastNotification';
import { AzureAuth } from '../Components/AzureAuth';
import DateTimeService from '@app/Services/DateTimeService';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { modalService } from '@app/Components/Modal';

class ApiHeaders {
    [key: string]: string | number;
}

export interface IAjaxOptions {
    responseType?: ResponseType;
    hideModalLoader?: boolean;
    requestKey?: string;
    transformDateTime?:boolean;
    operationName?:string;
    noDateTransform?: boolean;
    suppressErrorHandling?: boolean;
    completion?: PromiseCompletion;
}

type AxiosMethodDefinition = (url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise;
type AxiosMethodChooser = (instance: AxiosInstance) => AxiosMethodDefinition;

export default class ApiService {

    private static _callMethod<TResponse>(methodChooser: AxiosMethodChooser, url: string, data?: any, options?: IAjaxOptions): AxiosPromise<TResponse> {
        const responseType = options && options.responseType || 'json';
        const instance = ApiService._getInstance(responseType, options);
        const result = methodChooser(instance)(this._getAbsoluteUrl(url), data);
        const requestTimestamp = DateTimeService.today().getTime();
        const isHandleError = !(options && options.suppressErrorHandling);
        appStore.showAjaxSpinner(requestTimestamp);
        options && options.completion && options.completion.subscribe(result);
        result.catch((error: any) => {
            isHandleError && this.handleError(error);
            appStore.hideAjaxSpinner(requestTimestamp);
        });
        result.then(() => {
            appStore.hideAjaxSpinner(requestTimestamp);
        });
        return result;
    }

    public static getData(url: string, getData?: any, options?: IAjaxOptions): AxiosPromise {
        return this.getTypedData<any>(url, getData, options);
    }

    public static getTypedData<TResponse>(url: string, getData?: any, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.get, url, { params: getData }, options);
    }

    public static putData(url: string, putData?: any, options?: IAjaxOptions): AxiosPromise {
        return ApiService._callMethod((instance: AxiosInstance) => instance.put, url, putData, options);
    }

    public static putTypedData<TResponse>(url: string, putData?: any, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod((instance: AxiosInstance) => instance.put, url, putData, options);
    }

    public static postData(url: string, postData?: any, options?: IAjaxOptions): AxiosPromise {
        return ApiService._callMethod((instance: AxiosInstance) => instance.post, url, postData, options);
    }

    public static postTypedData<TResponse>(url: string, postData?: any, options?: IAjaxOptions): AxiosPromise<TResponse> {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.post, url, postData, options);
    }

    public static patchData(url: string, patchData?: any, options?: IAjaxOptions): AxiosPromise {
        return ApiService._callMethod((instance: AxiosInstance) => instance.patch, url, patchData, options);
    }

    public static deleteData(url: string, options?: IAjaxOptions): AxiosPromise {
        return ApiService._callMethod((instance: AxiosInstance) => instance.delete, url, void 0, options);
    }

    public static deleteTypedData<TResponse>(url: string, options?: IAjaxOptions): AxiosPromise {
        return ApiService._callMethod<TResponse>((instance: AxiosInstance) => instance.delete, url, void 0, options);
    }

    public static handleError(error: any) {
        if (error && error.response && error.response.status === 409) {
            return;
        }
        errorHandleService.showError(error);
    }

    public static toQueryString(params: { [key: string]: string }) {
        if (typeof(params) !== 'object') return '';
        return `?${Object.keys(params).map(k => `${k}=${params[k]}`).join('&')}`;
    }

    static typeCheck = (el: any, isDate?: boolean) => {
        if (!el) return el;
        switch (typeof el) {
            case 'string':
                el = ApiService.strCheck(el, isDate);
                break;
            case 'object':
                el = Array.isArray(el) ? ApiService.arrCheck(el) : ApiService.objCheck(el);
                break;
        }
        return el;
    };

    private static strCheck = (str: string, isDate?: boolean) => {
        if (isDate && DateTimeService.ISO_8601_date.test(str)) return DateTimeService.fromString(str);
        return str;
    };

    private static arrCheck = (array: any) => {
        return array.map((el: any) => {
            return ApiService.typeCheck(el);
        });
    };

    private static objCheck = (obj: any) => {
        Object.keys(obj).forEach(key => {
            obj[key] = ApiService.typeCheck(obj[key], key.indexOf('date') === 0 || key.indexOf('Date') !== -1);
        });
        return obj;
    };

    private static _getInstance(responseType: ResponseType = 'json', options?: IAjaxOptions): AxiosInstance {
        const headers = new ApiHeaders();
        headers['Cache-Control'] = 'no-cache';
        if (appStore.currentToken) {
            headers['Authorization'] = 'Bearer ' + appStore.currentToken;
        }
        if (appStore.impersonatedUser) {
            headers['App-Impersonation-PersonId'] = appStore.impersonatedUser.personId || '';
            headers['App-Impersonation-Role'] = appStore.impersonatedUser.role || '';
            if(appStore.impersonatedUser.impersonateAsPrincipal){
                headers['App-Impersonate-As-Principal'] = 'true';
            }
        }

        const transformResponse = (data: any) => {
            if (options && options.noDateTransform) return data;
            data = ApiService.typeCheck(data);
            return data;
        };

        const axiosInstance = axios.create({ responseType: responseType, headers: headers, transformResponse: transformResponse });
        axiosInstance.interceptors.response.use((res) => {
            const contentType = res.headers['content-type'];
            if (contentType && contentType.indexOf('text/html') > -1 && responseType === 'json') {
                throw new Error('API call to ' + res.config.url + ' returned not expected content type: ' + contentType);
            }
            return res;
        }, (err) => {
            if (err.response && err.response.status === 401) {
                return new Promise(() => {
                    AzureAuth.login();
                });
            }
            throw err;
        });

        return axiosInstance;
    }

    private static _getAbsoluteUrl(url: string): string {
        if (url && url[0] !== '/') url = '/' + url;
        if (!url.startsWith('/api')) url = '/api' + url;
        return location.origin + url;
    }
}

class ErrorHandleService {
    private _lastError: { message: string, time: number };

    showError(error: any) {
        const currentTime = (new Date()).getTime();
        console.log('--error AJAX: ', error);
        if (!this._lastError || this._lastError.message !== error.message || (currentTime - this._lastError.time > 10000)) {
            if (error.response && error.response.status === 403) {
                if (!/^\/403$/.test(window.location.pathname)) {
                    window.location.href = location.origin + '/403'; //can not use navigationStore because of problem with class instantiation
                }
            } else {
                NotificationHandler.showError(error.message, '--error AJAX');
                if (error && error.response && (error.response.status === 400 || error.response.status === 500)) {
                    modalService.showError(error);
                }
                this._lastError = { message: error.message, time: currentTime };
            }
        }
    }

}

const errorHandleService = new ErrorHandleService();